import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView, StatusBar, FlatList, Modal, TextInput, ActivityIndicator, RefreshControl, Alert, Dimensions, Image } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const API_BASE_URL = 'https://api.jobeng.ai';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token'); if (t) c.headers.Authorization = 'Bearer ' + t; const bid = await AsyncStorage.getItem('company_id'); if (bid) c.params = { ...c.params, company_id: parseInt(bid) }; } catch {} return c; }, e => Promise.reject(e));
const EP = { interviews: API_BASE_URL + '/api/data/application_interviews', applications: API_BASE_URL + '/api/data/applications', jobs: API_BASE_URL + '/api/data/jobs', employees: API_BASE_URL + '/api/data/employees', companyProfile: API_BASE_URL + '/api/data/company_profiles',
  completeInterview: (id) => API_BASE_URL + '/api/recruitment/interviews/' + id + '/complete',
  moveStage: (id) => API_BASE_URL + '/api/recruitment/applicant/' + id + '/stage' };
const safeData = r => (r?.data?.success && Array.isArray(r.data.data)) ? r.data.data : [];

export default function FinalInterviewPipelineScreen({ navigation, route }) {
  const jobId = route?.params?.jobId || null;
  const jobTitle = route?.params?.jobTitle || '';
  // When the screen is opened from QualifiedApplicantsScreen for a specific
  // candidate, the candidate envelope arrives in route.params. We auto-focus
  // them: if they don't yet have an in-person interview row in the company's
  // list, we still navigate forward into FinalInterview so the recruiter
  // can proceed. Otherwise the list view loads normally.
  const incomingCandidate = route?.params?.candidate || route?.params?.applicant || null;
  const incomingApplicantId = route?.params?.applicantId
                           || route?.params?.applicationId
                           || incomingCandidate?.id
                           || null;
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');
  const [companyId, setCompanyId] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('final_score');
  const [showARDemoModal, setShowARDemoModal] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  // Ref for the candidates FlatList so we can scrollToIndex when arriving
  // from another screen with a specific candidate in route.params.
  const flatListRef = useRef(null);

  // AR Demo scenarios
  const arDemoScenarios = [
    {
      id: 'product_demo',
      name: 'Product Demonstration',
      description: 'Present and demonstrate product features using AR',
      duration: 30,
      equipment: ['AR Headset', 'Motion Controllers', 'Tracking Cameras'],
      skills: ['Presentation', 'Product Knowledge', 'AR Navigation'],
    },
    {
      id: 'technical_solution',
      name: 'Technical Solution Presentation',
      description: 'Explain complex technical solutions in AR environment',
      duration: 45,
      equipment: ['AR Headset', '3D Models', 'Interactive Whiteboard'],
      skills: ['Technical Communication', 'Problem Solving', 'AR Interaction'],
    },
    {
      id: 'team_collaboration',
      name: 'Virtual Team Collaboration',
      description: 'Lead virtual team meeting in AR workspace',
      duration: 25,
      equipment: ['AR Headset', 'Virtual Meeting Room', 'Collaboration Tools'],
      skills: ['Leadership', 'Team Management', 'Virtual Communication'],
    },
    {
      id: 'design_review',
      name: 'Design Review Session',
      description: 'Review and critique 3D designs in AR space',
      duration: 35,
      equipment: ['AR Headset', '3D Design Models', 'Annotation Tools'],
      skills: ['Design Thinking', 'Critical Analysis', 'Spatial Reasoning'],
    },
  ];

  // Mock candidate data - 18 final interview candidates
  const generateMockCandidates = () => {
    const names = [
      'Sarah Johnson', 'Michael Chen', 'Emma Williams', 'James Rodriguez', 'Olivia Brown',
      'David Kim', 'Sophie Taylor', 'Alexander Davis', 'Isabella Garcia', 'William Wilson',
      'Ava Martinez', 'Benjamin Anderson', 'Mia Thompson', 'Lucas White', 'Charlotte Harris',
      'Henry Clark', 'Amelia Lewis', 'Sebastian Young',
    ];

    const positions = [
      'Senior Software Engineer', 'Product Manager', 'UX Designer', 'Data Scientist',
      'DevOps Engineer', 'Frontend Developer', 'Backend Developer', 'Full Stack Developer',
      'Mobile Developer', 'QA Engineer', 'Business Analyst', 'Project Manager',
    ];

    const companies = [
      'Google', 'Microsoft', 'Amazon', 'Apple', 'Meta', 'Netflix', 'Spotify', 'Uber',
      'Airbnb', 'Tesla', 'Stripe', 'Shopify', 'Slack', 'Adobe', 'Salesforce', 'Oracle',
    ];

    const locations = [
      'London, UK', 'Manchester, UK', 'Birmingham, UK', 'Edinburgh, UK', 'Bristol, UK',
      'Leeds, UK', 'Glasgow, UK', 'Liverpool, UK', 'Sheffield, UK', 'Newcastle, UK',
    ];

    const skills = [
      'React', 'Node.js', 'Python', 'Java', 'TypeScript', 'AWS', 'Docker', 'Kubernetes',
      'Machine Learning', 'Data Analysis', 'UI/UX Design', 'Project Management',
      'Agile', 'Scrum', 'Leadership', 'Communication', 'Problem Solving', 'Teamwork',
    ];

    const finalInterviewStatuses = ['scheduled', 'in_progress', 'completed', 'pending_ar', 'ar_completed'];
    const arDemoStatuses = ['not_assigned', 'assigned', 'in_progress', 'completed', 'excellent'];
    const executiveInterviewers = [
      'CEO - John Smith', 'CTO - Sarah Wilson', 'VP Engineering - Mike Johnson',
      'VP Product - Lisa Chen', 'Head of Design - David Brown', 'Chief Architect - Emma Davis',
    ];

    const mockCandidates = [];
    
    for (let i = 1; i <= 18; i++) {
      const name = names[i - 1];
      const position = positions[Math.floor(Math.random() * positions.length)];
      const company = companies[Math.floor(Math.random() * companies.length)];
      const location = locations[Math.floor(Math.random() * locations.length)];
      const finalInterviewStatus = finalInterviewStatuses[Math.floor(Math.random() * finalInterviewStatuses.length)];
      const arDemoStatus = arDemoStatuses[Math.floor(Math.random() * arDemoStatuses.length)];
      const interviewer = executiveInterviewers[Math.floor(Math.random() * executiveInterviewers.length)];
      
      const candidateSkills = [];
      const numSkills = Math.floor(Math.random() * 6) + 5; // 5-10 skills
      for (let j = 0; j < numSkills; j++) {
        const skill = skills[Math.floor(Math.random() * skills.length)];
        if (!candidateSkills.includes(skill)) {
          candidateSkills.push(skill);
        }
      }

      // Generate high-level scores (final interview candidates)
      const finalScore = Math.floor(Math.random() * 15) + 85; // 85-100
      const executiveScore = Math.floor(Math.random() * 15) + 85; // 85-100
      const presentationScore = Math.floor(Math.random() * 15) + 85; // 85-100
      const leadershipScore = Math.floor(Math.random() * 15) + 85; // 85-100
      const visionScore = Math.floor(Math.random() * 15) + 85; // 85-100
      const arDemoScore = arDemoStatus === 'completed' || arDemoStatus === 'excellent' ? 
                         Math.floor(Math.random() * 15) + 85 : null;

      // Generate AR Demo details
      const arDemoScenario = arDemoScenarios[Math.floor(Math.random() * arDemoScenarios.length)];
      const arDemoDate = new Date();
      arDemoDate.setDate(arDemoDate.getDate() + Math.floor(Math.random() * 5)); // Next 5 days

      // Generate final interview date
      const finalInterviewDate = new Date();
      finalInterviewDate.setDate(finalInterviewDate.getDate() + Math.floor(Math.random() * 7)); // Next week

      mockCandidates.push({
        id: `candidate_${i}`,
        name: name,
        email: `${name.toLowerCase().replace(' ', '.')}@email.com`,
        phone: `+44 ${Math.floor(Math.random() * 9000) + 1000} ${Math.floor(Math.random() * 900000) + 100000}`,
        position: position,
        currentCompany: company,
        location: location,
        experience: Math.floor(Math.random() * 10) + 8, // 8-18 years (very senior)
        education: ['Master\'s', 'PhD', 'MBA'][Math.floor(Math.random() * 3)],
        skills: candidateSkills,
        salaryExpectation: `£${(Math.floor(Math.random() * 50) + 80) * 1000}`, // £80k-£130k
        
        // Final interview details
        finalInterviewStatus: finalInterviewStatus,
        finalInterviewDate: finalInterviewDate.toISOString(),
        finalInterviewTime: ['09:00 AM', '10:30 AM', '02:00 PM', '03:30 PM'][Math.floor(Math.random() * 4)],
        interviewer: interviewer,
        interviewLocation: ['Executive Boardroom', 'CEO Office', 'Innovation Lab', 'Conference Room A'][Math.floor(Math.random() * 4)],
        
        // Final interview scores
        finalScore: finalInterviewStatus === 'completed' ? finalScore : null,
        executiveScore: finalInterviewStatus === 'completed' ? executiveScore : null,
        presentationScore: finalInterviewStatus === 'completed' ? presentationScore : null,
        leadershipScore: finalInterviewStatus === 'completed' ? leadershipScore : null,
        visionScore: finalInterviewStatus === 'completed' ? visionScore : null,
        
        // AR Demo details
        arDemoStatus: arDemoStatus,
        arDemoScenario: arDemoScenario,
        arDemoDate: arDemoStatus !== 'not_assigned' ? arDemoDate.toISOString() : null,
        arDemoScore: arDemoScore,
        arDemoFeedback: arDemoStatus === 'completed' || arDemoStatus === 'excellent' ? 
          'Excellent AR navigation and presentation skills. Demonstrated strong spatial awareness and technical communication.' : '',
        
        // AR Demo performance metrics
        arNavigationScore: arDemoScore ? Math.floor(Math.random() * 15) + 85 : null,
        arPresentationScore: arDemoScore ? Math.floor(Math.random() * 15) + 85 : null,
        arInteractionScore: arDemoScore ? Math.floor(Math.random() * 15) + 85 : null,
        arAdaptabilityScore: arDemoScore ? Math.floor(Math.random() * 15) + 85 : null,
        
        // Executive assessment
        executiveFeedback: finalInterviewStatus === 'completed' ? 
          'Outstanding candidate with exceptional leadership potential and strategic vision. Highly recommended for offer.' : '',
        ceoApproval: finalInterviewStatus === 'completed' && Math.random() > 0.3,
        boardRecommendation: finalInterviewStatus === 'completed' && finalScore >= 90,
        
        // Previous performance summary
        overallRating: Math.floor(Math.random() * 1) + 4, // 4-5 stars
        previousScores: {
          technical: Math.floor(Math.random() * 10) + 90, // 90-100
          interview: Math.floor(Math.random() * 10) + 90, // 90-100
          cultural: Math.floor(Math.random() * 10) + 90, // 90-100
          qualification: Math.floor(Math.random() * 10) + 90, // 90-100
        },
        
        // Final interview preparation
        executiveBriefing: Math.random() > 0.2,
        presentationPrepared: Math.random() > 0.1,
        referencesContacted: Math.random() > 0.3,
        backgroundCompleted: Math.random() > 0.2,
        
        // Offer preparation
        offerDraftReady: finalInterviewStatus === 'completed' && finalScore >= 88,
        salaryBenchmarked: Math.random() > 0.3,
        startDateDiscussed: Math.random() > 0.4,
        
        // AR Demo equipment and setup
        arEquipmentTested: arDemoStatus !== 'not_assigned' && Math.random() > 0.2,
        arScenarioCustomized: arDemoStatus !== 'not_assigned' && Math.random() > 0.3,
        arTechnicalSupport: arDemoStatus !== 'not_assigned',
        
        // Final interview logistics
        executiveCalendarBlocked: Math.random() > 0.2,
        interviewRoomBooked: Math.random() > 0.1,
        cateringArranged: Math.random() > 0.5,
        parkingReserved: Math.random() > 0.4,
        
        // Decision timeline
        decisionDeadline: new Date(Date.now() + Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
        offerTargetDate: new Date(Date.now() + Math.random() * 10 * 24 * 60 * 60 * 1000).toISOString(),
        
        // Tags based on performance and status
        tags: (() => {
          const baseTags = ['AR Demo'];
          if (finalScore >= 95) baseTags.push('Exceptional');
          if (arDemoStatus === 'excellent') baseTags.push('AR Expert');
          if (finalInterviewStatus === 'completed' && finalScore >= 90) baseTags.push('CEO Approved');
          return baseTags;
        })(),
        
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`,
        lastActivity: new Date(Date.now() - Math.random() * 12 * 60 * 60 * 1000).toISOString(),
      });
    }

    return mockCandidates.sort((a, b) => (b.finalScore || 0) - (a.finalScore || 0));
  };

  const tabs = [
    { id: 'all', name: 'All', count: 18 },
    { id: 'scheduled', name: 'Scheduled', count: 7 },
    { id: 'in_progress', name: 'In Progress', count: 2 },
    { id: 'completed', name: 'Completed', count: 6 },
    { id: 'ar_demo', name: 'AR Demo', count: 3 },
  ];

  const sortOptions = [
    { id: 'final_score', name: 'Final Score', icon: 'star' },
    { id: 'interview_date', name: 'Interview Date', icon: 'calendar' },
    { id: 'name', name: 'Name', icon: 'account' },
    { id: 'ar_score', name: 'AR Demo Score', icon: 'virtual-reality' },
    { id: 'executive_score', name: 'Executive Score', icon: 'crown' },
  ];

  useEffect(() => {
    loadCandidates();
  }, []);

  // When the screen is opened from QualifiedApplicantsScreen with a specific
  // candidate in route.params, we auto-focus them once the list loads. Two
  // possible outcomes:
  //   (a) Their interview row exists in the loaded list → select it for any
  //       inline actions, but stay here so the recruiter can review the
  //       in-person stage (scores, AR demo, location, etc).
  //   (b) Their interview row is missing → forward straight to FinalInterview
  //       so the recruiter can score them and progress the pipeline.
  // Either way the recruiter never has to find the candidate manually.
  useEffect(() => {
    if (!incomingCandidate || isLoading) return;
    const match = candidates.find(c =>
      String(c.applicationId) === String(incomingApplicantId) ||
      String(c.id)            === String(incomingApplicantId) ||
      (c.email && incomingCandidate.email && c.email === incomingCandidate.email)
    );
    console.log('[InPerson] auto-focus → incomingApplicantId=', incomingApplicantId,
                'incomingEmail=', incomingCandidate?.email,
                'matched=', match?.id || 'none');
    if (match) {
      setSelectedCandidate(match);
      // Visually mark the row as selected (checkbox toggles on) so the
      // recruiter immediately sees WHICH candidate they came in for.
      setSelectedCandidates([match.id]);
      // Scroll to it. Use the unsorted candidates index, then map to
      // sortedCandidates index since FlatList renders the sorted view.
      // If the index lookup fails we fall back to viewPosition 0.5 (centre).
      setTimeout(() => {
        const idx = (sortedCandidates || candidates).findIndex(c => String(c.id) === String(match.id));
        if (idx >= 0 && flatListRef.current) {
          flatListRef.current.scrollToIndex({ index: idx, viewPosition: 0.3, animated: true });
        }
      }, 250);
    } else if (incomingApplicantId) {
      // No in-person row yet — forward straight to FinalInterview with the
      // candidate envelope and the application id so the destination screen
      // can resolve the (possibly to-be-created) interview row.
      navigation.navigate('FinalInterviewDetailsScreen', {
        candidate: {
          ...incomingCandidate,
          interviewId:   incomingCandidate.interviewId,
          applicationId: incomingApplicantId,
        },
        interviewId:   incomingCandidate.interviewId || null,
        applicationId: incomingApplicantId,
        jobId, jobTitle, companyLogo, companyName,
      });
    }
  }, [isLoading, candidates.length, incomingApplicantId]);

  // ── Company Logo ──
  useFocusEffect(useCallback(() => { loadCompanyLogo(); }, []));
  const loadCompanyLogo = async () => {
    try { const cached = await AsyncStorage.getItem('company_profile'); if (cached) { const p = JSON.parse(cached); setCompanyLogo(p.logo || null); if (p.name || p.company_name) setCompanyName(p.name || p.company_name); }
      const cid = await AsyncStorage.getItem('company_id'); if (cid) { setCompanyId(cid); const r = await api.get(EP.companyProfile, { params: { company_id: cid } }); if (r.data?.success && r.data.data?.length > 0) { const lp = r.data.data[0]; setCompanyLogo(lp.logo || null); if (lp.name || lp.company_name) setCompanyName(lp.name || lp.company_name); } }
    } catch (e) { console.warn('Logo error:', e.message); }
  };

  // The candidates eligible for the in-person stage live in the `applications`
  // table (one row per candidate × job). The OLD loader read from
  // `application_interviews` — which only contains rows AFTER an interview
  // has been scheduled. That meant qualified candidates who hadn't been
  // booked yet were invisible in this screen, which broke the pipeline.
  //
  // Correct source: load applications at relevant pipeline stages (qualified,
  // shortlisted, in_person, interviewed, offer_ready) for this company. Then,
  // for each, merge their latest in-person/final/panel interview row from
  // application_interviews so the recruiter sees scores/dates/decisions when
  // they exist, plus the candidate's basic record when they don't.
  const IN_PERSON_STAGES = ['qualified', 'shortlisted', 'in_person', 'in-person', 'interview', 'interviewed', 'offer_ready'];

  const loadFromAPI = async () => {
    try {
      const cid = companyId || await AsyncStorage.getItem('company_id') || '1';

      // 1. Load applications (the candidate list)
      const [appsRes, ivsRes] = await Promise.allSettled([
        api.get(EP.applications, { params: { company_id: parseInt(cid), limit: 500 } }),
        api.get(EP.interviews,   { params: { company_id: parseInt(cid), limit: 1000 } }),
      ]);

      const apps = safeData(appsRes.status === 'fulfilled' ? appsRes.value : null);
      if (apps.length === 0) return false;

      // 2. Filter to in-person eligible stages
      const eligibleApps = apps.filter(a => {
        const stage = (a.stage || a.status || '').toLowerCase().replace(/\s+/g, '_');
        return IN_PERSON_STAGES.includes(stage);
      });
      if (eligibleApps.length === 0) {
        // No candidates at relevant stage yet — show empty rather than mock
        setCandidates([]);
        await AsyncStorage.setItem('inperson_interviews', JSON.stringify([]));
        return true;
      }

      // 3. Build index of interview rows by application_id (so we can merge
      //    in scores/decisions where they exist).
      const ivs = safeData(ivsRes.status === 'fulfilled' ? ivsRes.value : null);
      const ivByApp = new Map();
      for (const iv of ivs) {
        const t = (iv.interview_type || iv.type || '').toLowerCase();
        // Only consider in-person/final/panel for the score merge
        if (!['in-person', 'final', 'panel', 'in_person'].some(k => t.includes(k))) continue;
        const aid = iv.application_id;
        if (!aid) continue;
        const prev = ivByApp.get(aid);
        // Pick most recent by scheduled_date
        if (!prev || new Date(iv.scheduled_date || 0) > new Date(prev.scheduled_date || 0)) {
          ivByApp.set(aid, iv);
        }
      }

      // 4. Map each application to a card, optionally merging the interview row
      const mapped = eligibleApps.map(a => {
        const iv = ivByApp.get(a.id) || {};
        return {
          // Candidate identity comes from the application row
          id: a.id,                              // application id (so card actions can use it directly)
          applicationId: a.id,
          application_id: a.id,
          interviewId: iv.id || null,             // null when no interview row yet
          name: a.candidate_name || a.applicant_name || 'Unknown',
          email: a.candidate_email || '',
          phone: a.candidate_phone || '',
          position: a.current_title || a.job_title || jobTitle || '',
          currentCompany: a.current_company || '',
          location: a.candidate_location || '',
          experience: parseInt(a.experience_years || a.experience) || 0,
          stage: a.stage || a.status || '',
          appStatus: a.status || 'submitted',

          // Interview-specific fields (null when no interview row yet)
          interviewer: iv.interviewer_name || '',
          interviewLocation: iv.location || 'Office',
          finalInterviewDate: iv.scheduled_date || null,
          finalInterviewTime: iv.scheduled_date ? new Date(iv.scheduled_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
          finalInterviewStatus: iv.status || 'not_scheduled',
          finalScore: iv.rating || null,
          executiveScore: iv.executive_score || null,
          presentationScore: iv.presentation_score || null,
          leadershipScore: iv.leadership_score || null,
          visionScore: iv.vision_score || null,
          arDemoStatus: iv.ar_demo_status || 'not_assigned',
          arDemoScenario: { name: '', description: '', equipment: [], skills: [] },
          arDemoScore: iv.ar_demo_score || null,
          arNavigationScore: iv.ar_navigation_score || null,
          arPresentationScore: iv.ar_presentation_score || null,
          arInteractionScore: iv.ar_interaction_score || null,
          arAdaptabilityScore: iv.ar_adaptability_score || null,
          executiveFeedback: iv.executive_feedback || iv.feedback || '',
          feedback: iv.feedback || '',
          notes: iv.notes || '',
          ceoApproval: !!iv.ceo_approval,
          boardRecommendation: !!iv.board_recommendation,
          decision: iv.decision || null,

          // Display
          skills: Array.isArray(a.skills) ? a.skills : (typeof a.skills === 'string' ? a.skills.split(',').map(s => s.trim()).filter(Boolean) : []),
          tags: [],
          previousScores: {
            technical:    parseInt(a.assessment_score || a.test_score) || 0,
            interview:    parseInt(a.interview_score) || 0,
            cultural:     0,
            qualification: parseInt(a.ai_score || a.ai_match_score || a.overall_score) || 0,
          },
          overallRating: parseInt(a.ai_score || a.ai_match_score || a.overall_score) || 0,
          executiveBriefing: false,
          presentationPrepared: false,
          arEquipmentTested: false,
          interviewRoomBooked: !!iv.id,
          avatar: 'https://ui-avatars.com/api/?name=' + encodeURIComponent(a.candidate_name || 'U') + '&background=random',
          raw: a,
          rawInterview: iv,
        };
      });

      setCandidates(mapped);
      await AsyncStorage.setItem('inperson_interviews', JSON.stringify(mapped));
      return true;
    } catch (e) { console.warn('API load failed:', e.message); }
    return false;
  };

  const loadCandidates = async () => {
    setIsLoading(true);
    try {
      const apiLoaded = await loadFromAPI();
      if (apiLoaded) return;
      // API didn't return — try cache. NEVER fall through to mock data; show
      // empty rather than fake the pipeline.
      const cached = await AsyncStorage.getItem('inperson_interviews');
      if (cached) {
        try { setCandidates(JSON.parse(cached)); } catch { setCandidates([]); }
      } else {
        setCandidates([]);
      }
    } catch (error) {
      console.error('Error loading candidates:', error);
      setCandidates([]);
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadCandidates();
    setRefreshing(false);
  };

  const filteredCandidates = candidates.filter(candidate => {
    const matchesTab = selectedTab === 'all' || 
                      (selectedTab === 'ar_demo' && candidate.arDemoStatus !== 'not_assigned') ||
                      (selectedTab !== 'all' && selectedTab !== 'ar_demo' && candidate.finalInterviewStatus === selectedTab);
    
    const matchesSearch = (candidate.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (candidate.position || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (candidate.currentCompany || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (candidate.interviewer || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (candidate.interviewLocation || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (candidate.skills || []).some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesTab && matchesSearch;
  });

  const sortedCandidates = [...filteredCandidates].sort((a, b) => {
    switch (sortBy) {
      case 'final_score':
        return (b.finalScore || 0) - (a.finalScore || 0);
      case 'interview_date':
        return new Date(a.finalInterviewDate) - new Date(b.finalInterviewDate);
      case 'name':
        return a.name.localeCompare(b.name);
      case 'ar_score':
        return (b.arDemoScore || 0) - (a.arDemoScore || 0);
      case 'executive_score':
        return (b.executiveScore || 0) - (a.executiveScore || 0);
      default:
        return 0;
    }
  });

  const getFinalInterviewStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#3B82F6';
      case 'in_progress': return '#10B981';
      case 'completed': return '#059669';
      case 'pending_ar': return '#F59E0B';
      case 'ar_completed': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const getFinalInterviewStatusText = (status) => {
    switch (status) {
      case 'scheduled': return 'SCHEDULED';
      case 'in_progress': return 'IN PROGRESS';
      case 'completed': return 'COMPLETED';
      case 'pending_ar': return 'PENDING AR';
      case 'ar_completed': return 'AR COMPLETED';
      default: return (status || '').toUpperCase();
    }
  };

  const getARDemoStatusColor = (status) => {
    switch (status) {
      case 'not_assigned': return '#9CA3AF';
      case 'assigned': return '#3B82F6';
      case 'in_progress': return '#10B981';
      case 'completed': return '#059669';
      case 'excellent': return '#DC2626';
      default: return '#6B7280';
    }
  };

  const getARDemoStatusText = (status) => {
    switch (status) {
      case 'not_assigned': return 'NOT ASSIGNED';
      case 'assigned': return 'ASSIGNED';
      case 'in_progress': return 'IN PROGRESS';
      case 'completed': return 'COMPLETED';
      case 'excellent': return 'EXCELLENT';
      default: return (status || '').toUpperCase();
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString, timeString) => {
    return `${formatDate(dateString)} at ${timeString}`;
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <MaterialCommunityIcons
          key={i}
          name={i <= rating ? 'star' : 'star-outline'}
          size={14}
          color={i <= rating ? '#F59E0B' : '#D1D5DB'}
        />
      );
    }
    return stars;
  };

  const handleCandidatePress = (candidate) => {
    // Hand off to FinalInterviewDetailsScreen with the interview row id and
    // application id so both screens write to the SAME row in
    // application_interviews. The candidate envelope is normalised below.
    navigation.navigate('FinalInterviewDetailsScreen', {
      candidate: {
        ...candidate,
        interviewId:    candidate.interviewId    || candidate.id,
        applicationId:  candidate.applicationId  || candidate.application_id,
      },
      interviewId:    candidate.interviewId   || candidate.id,
      applicationId:  candidate.applicationId || candidate.application_id,
      jobId, jobTitle,
      companyLogo, companyName,
    });
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const handleStartARDemo = (candidate) => {
    Alert.alert(
      'Start AR Demo',
      `Start AR demonstration for ${candidate.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Start Demo', 
          onPress: async () => {
            setCandidates(prev => prev.map(c2 => c2.id === candidate.id ? { ...c2, arDemoStatus: 'in_progress' } : c2));
            try { await api.put(EP.interviews + '/' + candidate.id, { ar_demo_status: 'in_progress' }); } catch {}
            navigation.navigate('ARDemoScreen', { candidate, companyLogo, companyName, jobId, jobTitle });
          }
        },
      ]
    );
  };

  const handleJoinFinalInterview = (candidate) => {
    Alert.alert(
      'Join Final Interview',
      `Join executive interview with ${candidate.name}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Join', 
          onPress: () => {
            navigation.navigate('BusinessVideoInterviewScreen', { interviewId: candidate.id, applicantName: candidate.name, applicantEmail: candidate.email, position: candidate.position, interviewType: 'final', companyLogo, companyName, jobId, jobTitle });
          }
        },
      ]
    );
  };

  // ══════ NEW HANDLERS ══════
  const handleEvaluateCandidate = (candidate) => {
    navigation.navigate('EvaluationAssessmentScreen', { companyLogo, companyName, employeeId: candidate.id, employeeName: candidate.name, employeePosition: candidate.position, employeeEmail: candidate.email });
  };
  const handleScheduleFirstDay = (candidate) => {
    Alert.alert('Schedule First Day', 'Schedule first day for ' + (candidate.name || 'applicant') + '?', [{ text: 'Cancel' }, { text: 'Schedule', onPress: async () => {
      try { await api.post(EP.interviews, { candidate_name: candidate.name, candidate_email: candidate.email, position: candidate.position, type: 'First Day', status: 'Scheduled', company_id: parseInt(companyId) || 0 }); } catch {} Alert.alert('Scheduled', 'First day scheduled');
    } }]);
  };
  const handleSendFeedback = async (candidate) => {
    Alert.alert('Send Feedback', 'Send interview feedback to ' + (candidate.name || 'applicant') + '?', [{ text: 'Cancel' }, { text: 'Send', onPress: async () => {
      try { await api.put(EP.interviews + '/' + candidate.id, { feedback_sent: true, status: 'Completed' }); } catch {} Alert.alert('Sent', 'Feedback sent');
    } }]);
  };
  // ── Pipeline progression handlers ──
  // "Pass" the candidate from the in-person stage → mark the interview row's
  // decision='pass', bump the applicant stage to 'offer_ready', then forward
  // to JobOfferManagementScreen — the canonical place for handling job offers
  // (create, send, track, accept, decline).
  const handlePassToOffer = async (candidate) => {
    // Only treat as a real interview-row update when interviewId is
    // explicitly populated. Falling back to candidate.id here was wrong —
    // candidate.id is the applications row id, and PUT to
    // /api/data/application_interviews/<applicationId> 404s because no row
    // with that id exists. Without a real interview row we just skip the
    // PUT and proceed with the stage bump.
    const ivId  = candidate.interviewId || null;
    const appId = candidate.applicationId || candidate.application_id;
    Alert.alert(
      'Pass candidate',
      `Mark ${candidate.name || 'this candidate'} as passing the in-person interview and proceed to job offer?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Pass & Make Offer', onPress: async () => {
          // 1. Mark interview row as passed — only when one actually exists
          if (ivId) {
            try { await api.put(EP.interviews + '/' + ivId, { status: 'completed', decision: 'pass' }); }
            catch (e) { console.warn('pass row update:', e?.message); }
          } else {
            console.log('[InPerson] no interviewId for', candidate.name, '— skipping row update, doing stage bump only');
          }
          // 2. Bump applicant stage to 'offer_ready' so pipeline view reflects
          if (appId) {
            try {
              await api.post(EP.moveStage(appId), {
                stage:  'offer_ready',
                status: 'in_person_passed',
                note:   `Passed in-person interview — ${candidate.name || ''} promoted to offer stage`,
              });
            } catch (e) { console.warn('pass stage bump:', e?.message); }
          }
          // 3. Mirror locally so the list reflects the change without a refetch
          setCandidates(prev => prev.map(c2 =>
            c2.id === candidate.id ? { ...c2, finalInterviewStatus: 'completed', decision: 'pass' } : c2
          ));
          // 4. Forward to MakeOfferScreen with the candidate envelope
          navigation.navigate('JobOfferManagementScreen', {
            candidate: {
              ...candidate,
              applicationId: appId,
              jobId:    candidate.jobId    || jobId,
              jobTitle: candidate.jobTitle || jobTitle,
            },
            applicant:   { ...candidate, applicationId: appId, jobId, jobTitle },
            applicantId: appId,
            jobId, jobTitle, companyLogo, companyName,
          });
        } }
      ]
    );
  };

  // Direct shortcut to JobOfferManagement without explicitly passing the
  // in-person stage (e.g. for a candidate we've already decided about).
  // Same destination as handlePassToOffer but without the DB stage bump.
  const handleMakeOfferNav = (candidate) => {
    const appId = candidate.applicationId || candidate.application_id;
    navigation.navigate('JobOfferManagementScreen', {
      candidate: { ...candidate, applicationId: appId, jobId: candidate.jobId || jobId, jobTitle: candidate.jobTitle || jobTitle },
      applicant: { ...candidate, applicationId: appId, jobId, jobTitle },
      applicantId: appId,
      jobId, jobTitle, companyLogo, companyName,
    });
  };

  // Keep legacy alias so any existing UI still works
  const handleMarkHired = handlePassToOffer;

  const handleRejectCandidate = async (candidate) => {
    const ivId  = candidate.interviewId || null;
    const appId = candidate.applicationId || candidate.application_id;
    Alert.alert(
      'Reject',
      `Reject ${candidate.name || 'this candidate'}?`,
      [
        { text: 'Cancel' },
        { text: 'Reject', style: 'destructive', onPress: async () => {
          if (ivId) { try { await api.put(EP.interviews + '/' + ivId, { status: 'completed', decision: 'reject' }); } catch {} }
          if (appId) {
            try {
              await api.post(EP.moveStage(appId), {
                stage:  'rejected',
                status: 'in_person_rejected',
                note:   'Rejected after in-person interview',
              });
            } catch {}
          }
          setCandidates(prev => prev.map(c2 =>
            c2.id === candidate.id ? { ...c2, finalInterviewStatus: 'completed', decision: 'reject' } : c2
          ));
        } }
      ]
    );
  };

  const handleBulkAction = (action) => {
    if (selectedCandidates.length === 0) {
      Alert.alert('No Selection', 'Please select applicants first');
      return;
    }
    Alert.alert(
      'Bulk Action',
      `${action} ${selectedCandidates.length} selected candidate${selectedCandidates.length === 1 ? '' : 's'}?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          onPress: async () => {
            // For each selected, mark the interview row appropriately + bump stage.
            const passing = action === 'Move to Job Offers';
            for (const cid2 of selectedCandidates) {
              const target = candidates.find(c => c.id === cid2);
              if (!target) continue;
              const ivId  = target.interviewId || null;
              const appId = target.applicationId || target.application_id;
              if (ivId) {
                try { await api.put(EP.interviews + '/' + ivId, { status: 'completed', decision: passing ? 'pass' : 'reject' }); } catch {}
              }
              if (appId) {
                try {
                  await api.post(EP.moveStage(appId), {
                    stage:  passing ? 'offer_ready' : 'rejected',
                    status: passing ? 'in_person_passed' : 'in_person_rejected',
                    note:   passing ? 'Bulk: passed in-person' : 'Bulk: rejected in-person',
                  });
                } catch {}
              }
            }

            const first = selectedCandidates[0];
            const firstCand = candidates.find(c => c.id === first);
            setSelectedCandidates([]);

            // Forward the first passing candidate into JobOfferManagement so
            // the recruiter can build/track the offer immediately. The rest
            // are stage-updated server-side and will appear in JobOfferManagement's
            // candidate list (loads by stage='offer_ready').
            if (passing && firstCand) {
              handleMakeOfferNav(firstCand);
            }
          }
        },
      ]
    );
  };

  const renderCandidateCard = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.candidateCard,
        selectedCandidates.includes(item.id) && styles.selectedCard,
      ]}
      onPress={() => handleCandidatePress(item)}
      activeOpacity={0.7}
    >
      {/* Selection Checkbox */}
      <TouchableOpacity
        style={styles.checkbox}
        onPress={() => handleSelectCandidate(item.id)}
      >
        <MaterialCommunityIcons
          name={selectedCandidates.includes(item.id) ? 'checkbox-marked' : 'checkbox-blank-outline'}
          size={24}
          color={selectedCandidates.includes(item.id) ? '#EF4444' : '#D1D5DB'}
        />
      </TouchableOpacity>

      {/* Candidate Header */}
      <View style={styles.candidateHeader}>
        <Image source={{ uri: (item.avatar && item.avatar.startsWith('http')) ? item.avatar : undefined }} style={styles.avatar} />
        <View style={styles.candidateInfo}>
          <View style={styles.candidateNameContainer}>
            <Text style={styles.candidateName}>{item.name}</Text>
            <View style={styles.candidateBadges}>
              {(item.tags || []).map((tag, index) => (
                <View key={index} style={styles.tagBadge}>
                  <MaterialCommunityIcons name="virtual-reality" size={10} color="#EF4444" />
                  <Text style={styles.tagText}>{tag}</Text>
                </View>
              ))}
            </View>
          </View>
          <Text style={styles.candidatePosition}>{item.position}</Text>
          <Text style={styles.candidateCompany}>{item.currentCompany} • {item.location}</Text>
        </View>
        <View style={styles.candidateStatus}>
          <View style={[styles.statusBadge, { backgroundColor: getFinalInterviewStatusColor(item.finalInterviewStatus) }]}>
            <Text style={styles.statusText}>{getFinalInterviewStatusText(item.finalInterviewStatus)}</Text>
          </View>
        </View>
      </View>

      {/* Final Interview Information */}
      <View style={styles.finalInterviewContainer}>
        <View style={styles.finalInterviewHeader}>
          <MaterialCommunityIcons name="crown" size={20} color="#EF4444" />
          <Text style={styles.finalInterviewTitle}>Executive Final Interview</Text>
          <View style={styles.executiveBadge}>
            <MaterialCommunityIcons name="account-tie" size={12} color="#FFFFFF" />
            <Text style={styles.executiveText}>C-Level</Text>
          </View>
        </View>
        
        {/* Interview Schedule */}
        <View style={styles.interviewSchedule}>
          <View style={styles.scheduleItem}>
            <MaterialCommunityIcons name="calendar" size={16} color="#6B7280" />
            <Text style={styles.scheduleText}>{formatTime(item.finalInterviewDate, item.finalInterviewTime)}</Text>
          </View>
          <View style={styles.scheduleItem}>
            <MaterialCommunityIcons name="account-star" size={16} color="#6B7280" />
            <Text style={styles.scheduleText}>{item.interviewer}</Text>
          </View>
          <View style={styles.scheduleItem}>
            <MaterialCommunityIcons name="office-building" size={16} color="#6B7280" />
            <Text style={styles.scheduleText}>{item.interviewLocation}</Text>
          </View>
        </View>
      </View>

      {/* AR Demo Section */}
      <View style={styles.arDemoContainer}>
        <View style={styles.arDemoHeader}>
          <MaterialCommunityIcons name="virtual-reality" size={20} color="#8B5CF6" />
          <Text style={styles.arDemoTitle}>AR Demonstration</Text>
          <View style={[styles.arDemoStatusBadge, { backgroundColor: getARDemoStatusColor(item.arDemoStatus) }]}>
            <Text style={styles.arDemoStatusText}>{getARDemoStatusText(item.arDemoStatus)}</Text>
          </View>
        </View>
        
        {/* AR Demo Details */}
        <View style={styles.arDemoDetails}>
          <Text style={styles.arDemoScenarioName}>{(item.arDemoScenario || {}).name}</Text>
          <Text style={styles.arDemoDescription}>{(item.arDemoScenario || {}).description}</Text>
          
          {/* AR Demo Equipment */}
          <View style={styles.arEquipmentContainer}>
            <Text style={styles.arEquipmentTitle}>Required Equipment:</Text>
            <View style={styles.arEquipmentList}>
              {(item.arDemoScenario?.equipment || []).map((equipment, index) => (
                <View key={index} style={styles.equipmentBadge}>
                  <MaterialCommunityIcons name="check-circle" size={12} color="#8B5CF6" />
                  <Text style={styles.equipmentText}>{equipment}</Text>
                </View>
              ))}
            </View>
          </View>
          
          {/* AR Demo Skills */}
          <View style={styles.arSkillsContainer}>
            <Text style={styles.arSkillsTitle}>Skills Assessed:</Text>
            <View style={styles.arSkillsList}>
              {(item.arDemoScenario?.skills || []).map((skill, index) => (
                <View key={index} style={styles.skillBadge}>
                  <Text style={styles.skillText}>{skill}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>

      {/* Final Interview Scores */}
      {item.finalInterviewStatus === 'completed' && (
        <View style={styles.finalScoresContainer}>
          <View style={styles.finalScoresHeader}>
            <Text style={styles.finalScoresTitle}>Executive Assessment</Text>
            <Text style={[
              styles.finalOverallScore,
              { color: item.finalScore >= 95 ? '#DC2626' : item.finalScore >= 90 ? '#10B981' : '#F59E0B' }
            ]}>
              {item.finalScore}%
            </Text>
          </View>
          
          <View style={styles.scoreBreakdown}>
            <View style={styles.scoreItem}>
              <MaterialCommunityIcons name="crown" size={16} color="#EF4444" />
              <Text style={styles.scoreLabel}>Executive</Text>
              <Text style={styles.scoreValue}>{item.executiveScore}%</Text>
            </View>
            <View style={styles.scoreItem}>
              <MaterialCommunityIcons name="presentation" size={16} color="#3B82F6" />
              <Text style={styles.scoreLabel}>Presentation</Text>
              <Text style={styles.scoreValue}>{item.presentationScore}%</Text>
            </View>
            <View style={styles.scoreItem}>
              <MaterialCommunityIcons name="account-group" size={16} color="#10B981" />
              <Text style={styles.scoreLabel}>Leadership</Text>
              <Text style={styles.scoreValue}>{item.leadershipScore}%</Text>
            </View>
            <View style={styles.scoreItem}>
              <MaterialCommunityIcons name="lightbulb" size={16} color="#F59E0B" />
              <Text style={styles.scoreLabel}>Vision</Text>
              <Text style={styles.scoreValue}>{item.visionScore}%</Text>
            </View>
          </View>
        </View>
      )}

      {/* AR Demo Scores */}
      {item.arDemoScore && (
        <View style={styles.arScoresContainer}>
          <View style={styles.arScoresHeader}>
            <MaterialCommunityIcons name="virtual-reality" size={18} color="#8B5CF6" />
            <Text style={styles.arScoresTitle}>AR Demo Performance</Text>
            <Text style={[
              styles.arOverallScore,
              { color: item.arDemoScore >= 95 ? '#DC2626' : item.arDemoScore >= 90 ? '#8B5CF6' : '#F59E0B' }
            ]}>
              {item.arDemoScore}%
            </Text>
          </View>
          
          <View style={styles.arScoreBreakdown}>
            <View style={styles.arScoreItem}>
              <MaterialCommunityIcons name="navigation" size={14} color="#8B5CF6" />
              <Text style={styles.arScoreLabel}>Navigation</Text>
              <Text style={styles.arScoreValue}>{item.arNavigationScore}%</Text>
            </View>
            <View style={styles.arScoreItem}>
              <MaterialCommunityIcons name="presentation" size={14} color="#8B5CF6" />
              <Text style={styles.arScoreLabel}>Presentation</Text>
              <Text style={styles.arScoreValue}>{item.arPresentationScore}%</Text>
            </View>
            <View style={styles.arScoreItem}>
              <MaterialCommunityIcons name="gesture-tap" size={14} color="#8B5CF6" />
              <Text style={styles.arScoreLabel}>Interaction</Text>
              <Text style={styles.arScoreValue}>{item.arInteractionScore}%</Text>
            </View>
            <View style={styles.arScoreItem}>
              <MaterialCommunityIcons name="refresh" size={14} color="#8B5CF6" />
              <Text style={styles.arScoreLabel}>Adaptability</Text>
              <Text style={styles.arScoreValue}>{item.arAdaptabilityScore}%</Text>
            </View>
          </View>
        </View>
      )}

      {/* Executive Feedback */}
      {item.executiveFeedback && (
        <View style={styles.feedbackContainer}>
          <MaterialCommunityIcons name="comment-quote" size={16} color="#EF4444" />
          <Text style={styles.feedbackText}>{item.executiveFeedback}</Text>
        </View>
      )}

      {/* CEO Approval & Board Recommendation */}
      {item.finalInterviewStatus === 'completed' && (
        <View style={styles.approvalContainer}>
          <View style={styles.approvalItem}>
            <MaterialCommunityIcons 
              name={item.ceoApproval ? 'check-circle' : 'clock-outline'} 
              size={16} 
              color={item.ceoApproval ? '#10B981' : '#F59E0B'} 
            />
            <Text style={styles.approvalText}>
              CEO Approval: {item.ceoApproval ? 'Approved' : 'Pending'}
            </Text>
          </View>
          <View style={styles.approvalItem}>
            <MaterialCommunityIcons 
              name={item.boardRecommendation ? 'star-circle' : 'star-circle-outline'} 
              size={16} 
              color={item.boardRecommendation ? '#DC2626' : '#9CA3AF'} 
            />
            <Text style={styles.approvalText}>
              Board Recommendation: {item.boardRecommendation ? 'Recommended' : 'Under Review'}
            </Text>
          </View>
        </View>
      )}

      {/* Previous Performance Summary */}
      <View style={styles.previousPerformanceContainer}>
        <Text style={styles.previousPerformanceTitle}>Previous Performance Summary</Text>
        <View style={styles.previousScores}>
          <View style={styles.previousScoreItem}>
            <Text style={styles.previousScoreLabel}>Technical</Text>
            <Text style={styles.previousScoreValue}>{(item.previousScores || {}).technical || 0}%</Text>
          </View>
          <View style={styles.previousScoreItem}>
            <Text style={styles.previousScoreLabel}>Interview</Text>
            <Text style={styles.previousScoreValue}>{(item.previousScores || {}).interview || 0}%</Text>
          </View>
          <View style={styles.previousScoreItem}>
            <Text style={styles.previousScoreLabel}>Cultural</Text>
            <Text style={styles.previousScoreValue}>{(item.previousScores || {}).cultural || 0}%</Text>
          </View>
          <View style={styles.previousScoreItem}>
            <Text style={styles.previousScoreLabel}>Qualification</Text>
            <Text style={styles.previousScoreValue}>{(item.previousScores || {}).qualification || 0}%</Text>
          </View>
        </View>
        
        {/* Overall Rating */}
        <View style={styles.overallRatingContainer}>
          <Text style={styles.overallRatingLabel}>Overall Rating:</Text>
          <View style={styles.starsContainer}>
            {renderStars(item.overallRating)}
          </View>
          <Text style={styles.ratingText}>({item.overallRating}/5)</Text>
        </View>
      </View>

      {/* Preparation Status */}
      <View style={styles.preparationContainer}>
        <Text style={styles.preparationTitle}>Interview Preparation</Text>
        <View style={styles.preparationItems}>
          <View style={styles.preparationItem}>
            <MaterialCommunityIcons 
              name={item.executiveBriefing ? 'check-circle' : 'clock-outline'} 
              size={14} 
              color={item.executiveBriefing ? '#10B981' : '#F59E0B'} 
            />
            <Text style={styles.preparationText}>Executive Briefing</Text>
          </View>
          <View style={styles.preparationItem}>
            <MaterialCommunityIcons 
              name={item.presentationPrepared ? 'check-circle' : 'clock-outline'} 
              size={14} 
              color={item.presentationPrepared ? '#10B981' : '#F59E0B'} 
            />
            <Text style={styles.preparationText}>Presentation Ready</Text>
          </View>
          <View style={styles.preparationItem}>
            <MaterialCommunityIcons 
              name={item.arEquipmentTested ? 'check-circle' : 'clock-outline'} 
              size={14} 
              color={item.arEquipmentTested ? '#10B981' : '#F59E0B'} 
            />
            <Text style={styles.preparationText}>AR Equipment Tested</Text>
          </View>
          <View style={styles.preparationItem}>
            <MaterialCommunityIcons 
              name={item.interviewRoomBooked ? 'check-circle' : 'clock-outline'} 
              size={14} 
              color={item.interviewRoomBooked ? '#10B981' : '#F59E0B'} 
            />
            <Text style={styles.preparationText}>Room Booked</Text>
          </View>
        </View>
      </View>

      {/* Actions */}
      <View style={styles.candidateActions}>
        {item.finalInterviewStatus === 'in_progress' && (
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: '#10B981' }]}
            onPress={() => handleJoinFinalInterview(item)}
          >
            <MaterialCommunityIcons name="video" size={16} color="#FFFFFF" />
            <Text style={[styles.actionButtonText, { color: '#FFFFFF' }]}>Join Interview</Text>
          </TouchableOpacity>
        )}
        {item.arDemoStatus === 'assigned' && (
          <TouchableOpacity
            style={[styles.actionButton, { backgroundColor: '#8B5CF6' }]}
            onPress={() => handleStartARDemo(item)}
          >
            <MaterialCommunityIcons name="virtual-reality" size={16} color="#FFFFFF" />
            <Text style={[styles.actionButtonText, { color: '#FFFFFF' }]}>Start AR Demo</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => navigation.navigate('FinalInterviewDetailsScreen', {
            candidate: {
              ...item,
              interviewId:   item.interviewId   || item.id,
              applicationId: item.applicationId || item.application_id,
            },
            interviewId:   item.interviewId   || item.id,
            applicationId: item.applicationId || item.application_id,
            jobId, jobTitle, companyLogo, companyName,
          })}
        >
          <MaterialCommunityIcons name="eye" size={16} color="#EF4444" />
          <Text style={styles.actionButtonText}>View Details</Text>
        </TouchableOpacity>
        {/* "Pass" — promotes the candidate from in-person to the offer stage.
            Marks the interview row as completed + decision=pass, bumps the
            applicant's pipeline stage to 'offer_ready', then forwards to
            MakeOfferScreen so the recruiter can build the offer. Always
            visible — the recruiter decides who passes, not the score gate. */}
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => handlePassToOffer(item)}
        >
          <MaterialCommunityIcons name="check-circle" size={16} color="#10B981" />
          <Text style={styles.actionButtonText}>Pass</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => handleRejectCandidate(item)}
        >
          <MaterialCommunityIcons name="close-circle" size={16} color="#EF4444" />
          <Text style={styles.actionButtonText}>Reject</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.moreButton}
          onPress={() => {
            Alert.alert(
              'In-Person Interview Actions',
              'Choose an action',
              [
                { text: 'Schedule AR Demo', onPress: () => navigation.navigate('ARDemoScreen', { candidate: item, companyLogo, companyName, jobId, jobTitle }) },
                { text: 'Evaluate', onPress: () => handleEvaluateCandidate(item) },
                { text: 'Make Offer (skip pass)', onPress: () => handleMakeOfferNav(item) },
                { text: 'Open in Final Interview', onPress: () => navigation.navigate('FinalInterviewDetailsScreen', {
                    candidate: { ...item, interviewId: item.interviewId || item.id, applicationId: item.applicationId || item.application_id },
                    interviewId: item.interviewId || item.id,
                    applicationId: item.applicationId || item.application_id,
                    jobId, jobTitle, companyLogo, companyName,
                }) },
                { text: 'Cancel', style: 'cancel' },
              ]
            );
          }}
        >
          <MaterialCommunityIcons name="dots-vertical" size={20} color="#6B7280" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#EF4444" />
          <Text style={styles.loadingText}>Loading final interviews...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#EF4444" />
      
      {/* Header */}
      <LinearGradient
        colors={['#EF4444', '#DC2626']}
        style={styles.header}
      >
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View style={styles.headerInfo}>
            <View style={styles.headerTitleContainer}>
              {companyLogo ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 24, height: 24, borderRadius: 12 }} /> : <MaterialCommunityIcons name="crown" size={24} color="#FFFFFF" />}
              <Text style={styles.headerTitle}>Final Interview</Text>
            </View>
            <Text style={styles.headerSubtitle}>
              {sortedCandidates.length} Applicants • AR Demo
            </Text>
          </View>
          
          <TouchableOpacity
            style={styles.arDemoButton}
            onPress={() => navigation.navigate('ARDemoScreen', { companyLogo, companyName, jobId, jobTitle })}
          >
            <MaterialCommunityIcons name="virtual-reality" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Search and Sort */}
      <View style={styles.searchSortContainer}>
        <View style={styles.searchBar}>
          <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search final interviews..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#9CA3AF"
          />
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.sortContainer}
        >
          {sortOptions.map((option) => (
            <TouchableOpacity
              key={option.id}
              style={[
                styles.sortOption,
                sortBy === option.id && styles.activeSortOption,
              ]}
              onPress={() => setSortBy(option.id)}
            >
              <MaterialCommunityIcons
                name={option.icon}
                size={16}
                color={sortBy === option.id ? '#FFFFFF' : '#6B7280'}
              />
              <Text
                style={[
                  styles.sortOptionText,
                  sortBy === option.id && styles.activeSortOptionText,
                ]}
              >
                {option.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabsContent}
        >
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.tab,
                selectedTab === tab.id && styles.activeTab,
              ]}
              onPress={() => setSelectedTab(tab.id)}
            >
              <Text
                style={[
                  styles.tabText,
                  selectedTab === tab.id && styles.activeTabText,
                ]}
              >
                {tab.name}
              </Text>
              <View
                style={[
                  styles.tabBadge,
                  selectedTab === tab.id && styles.activeTabBadge,
                ]}
              >
                <Text
                  style={[
                    styles.tabBadgeText,
                    selectedTab === tab.id && styles.activeTabBadgeText,
                  ]}
                >
                  {selectedTab === tab.id ? sortedCandidates.length : tab.count}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Bulk Actions */}
      {selectedCandidates.length > 0 && (
        <View style={styles.bulkActionsContainer}>
          <Text style={styles.bulkActionsText}>
            {selectedCandidates.length} selected
          </Text>
          <View style={styles.bulkActions}>
            <TouchableOpacity
              style={styles.bulkActionButton}
              onPress={() => navigation.navigate('ARDemoScreen', { companyLogo, companyName, jobId, jobTitle })}
            >
              <MaterialCommunityIcons name="virtual-reality" size={16} color="#FFFFFF" />
              <Text style={styles.bulkActionText}>AR Demo</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.bulkActionButton, { backgroundColor: '#10B981' }]}
              onPress={() => handleBulkAction('Move to Job Offers')}
            >
              <MaterialCommunityIcons name="arrow-right" size={16} color="#FFFFFF" />
              <Text style={styles.bulkActionText}>Move to Offers</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Candidates List */}
      <FlatList
        ref={flatListRef}
        data={sortedCandidates}
        keyExtractor={(item) => item.id}
        renderItem={renderCandidateCard}
        style={styles.candidatesList}
        contentContainerStyle={styles.candidatesListContent}
        showsVerticalScrollIndicator={false}
        onScrollToIndexFailed={(info) => {
          // Defensive fallback for when the row hasn't been measured yet —
          // wait a frame and retry with a safer offset.
          setTimeout(() => {
            flatListRef.current?.scrollToOffset({ offset: info.averageItemLength * info.index, animated: true });
          }, 100);
        }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={() => (
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="crown-outline" size={64} color="#D1D5DB" />
            <Text style={styles.emptyTitle}>No final interviews found</Text>
            <Text style={styles.emptySubtitle}>
              {searchQuery
                ? 'Try adjusting your search criteria'
                : 'No interviews match the current criteria'}
            </Text>
          </View>
        )}
      />

      {/* Floating Action Button */}
      <TouchableOpacity
        style={styles.fab}
        onPress={() => navigation.navigate('JobOfferManagementScreen', {
          totalCandidates: sortedCandidates.filter(c => c.finalInterviewStatus === 'completed').length,
        })}
      >
        <MaterialCommunityIcons name="arrow-right" size={24} color="#FFFFFF" />
      </TouchableOpacity>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
  },
  header: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  arDemoButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchSortContainer: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 12,
  },
  searchInput: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: '#1F2937',
  },
  sortContainer: {
    flexDirection: 'row',
  },
  sortOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
  },
  activeSortOption: {
    backgroundColor: '#EF4444',
  },
  sortOptionText: {
    marginLeft: 4,
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  activeSortOptionText: {
    color: '#FFFFFF',
  },
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabsContent: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
  },
  activeTab: {
    backgroundColor: '#FEE2E2',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },
  activeTabText: {
    color: '#EF4444',
  },
  tabBadge: {
    marginLeft: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: '#E5E7EB',
  },
  activeTabBadge: {
    backgroundColor: '#EF4444',
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
  },
  activeTabBadgeText: {
    color: '#FFFFFF',
  },
  bulkActionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#EF4444',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  bulkActionsText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  bulkActions: {
    flexDirection: 'row',
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginLeft: 8,
  },
  bulkActionText: {
    marginLeft: 4,
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  candidatesList: {
    flex: 1,
  },
  candidatesListContent: {
    padding: 20,
  },
  candidateCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  selectedCard: {
    borderWidth: 2,
    borderColor: '#EF4444',
  },
  checkbox: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 1,
  },
  candidateHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    marginRight: 12,
  },
  candidateInfo: {
    flex: 1,
  },
  candidateNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  candidateName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginRight: 8,
  },
  candidateBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tagBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#FEE2E2',
    borderRadius: 8,
    marginRight: 4,
    marginBottom: 2,
  },
  tagText: {
    marginLeft: 2,
    fontSize: 10,
    fontWeight: '600',
    color: '#EF4444',
  },
  candidatePosition: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 2,
  },
  candidateCompany: {
    fontSize: 12,
    color: '#6B7280',
  },
  candidateStatus: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  finalInterviewContainer: {
    backgroundColor: '#FEE2E2',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#EF4444',
  },
  finalInterviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  finalInterviewTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  executiveBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#EF4444',
    borderRadius: 12,
  },
  executiveText: {
    marginLeft: 4,
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  interviewSchedule: {
    marginTop: 8,
  },
  scheduleItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  scheduleText: {
    marginLeft: 8,
    fontSize: 12,
    color: '#374151',
  },
  arDemoContainer: {
    backgroundColor: '#F3E8FF',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#8B5CF6',
  },
  arDemoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  arDemoTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  arDemoStatusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  arDemoStatusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  arDemoDetails: {
    marginTop: 8,
  },
  arDemoScenarioName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  arDemoDescription: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  arEquipmentContainer: {
    marginBottom: 8,
  },
  arEquipmentTitle: {
    fontSize: 11,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  arEquipmentList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  equipmentBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#EDE9FE',
    borderRadius: 8,
    marginRight: 4,
    marginBottom: 2,
  },
  equipmentText: {
    marginLeft: 2,
    fontSize: 10,
    color: '#8B5CF6',
    fontWeight: '500',
  },
  arSkillsContainer: {
    marginBottom: 4,
  },
  arSkillsTitle: {
    fontSize: 11,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  arSkillsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  skillBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#F3E8FF',
    borderRadius: 8,
    marginRight: 4,
    marginBottom: 2,
  },
  skillText: {
    fontSize: 10,
    color: '#8B5CF6',
    fontWeight: '500',
  },
  finalScoresContainer: {
    backgroundColor: '#FEF2F2',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  finalScoresHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  finalScoresTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  finalOverallScore: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  scoreBreakdown: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  scoreItem: {
    alignItems: 'center',
  },
  scoreLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  scoreValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 2,
  },
  arScoresContainer: {
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  arScoresHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  arScoresTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  arOverallScore: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  arScoreBreakdown: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  arScoreItem: {
    alignItems: 'center',
  },
  arScoreLabel: {
    fontSize: 9,
    color: '#6B7280',
    marginTop: 2,
  },
  arScoreValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 2,
  },
  feedbackContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FEF2F2',
    padding: 8,
    borderRadius: 8,
    marginBottom: 12,
  },
  feedbackText: {
    marginLeft: 8,
    fontSize: 12,
    color: '#EF4444',
    fontStyle: 'italic',
    flex: 1,
  },
  approvalContainer: {
    marginBottom: 12,
  },
  approvalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  approvalText: {
    marginLeft: 8,
    fontSize: 12,
    color: '#374151',
    fontWeight: '500',
  },
  previousPerformanceContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  previousPerformanceTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  previousScores: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  previousScoreItem: {
    alignItems: 'center',
  },
  previousScoreLabel: {
    fontSize: 10,
    color: '#6B7280',
  },
  previousScoreValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#10B981',
    marginTop: 2,
  },
  overallRatingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  overallRatingLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginRight: 8,
  },
  starsContainer: {
    flexDirection: 'row',
    marginRight: 8,
  },
  ratingText: {
    fontSize: 12,
    color: '#F59E0B',
    fontWeight: '600',
  },
  preparationContainer: {
    marginBottom: 12,
  },
  preparationTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  preparationItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  preparationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 4,
  },
  preparationText: {
    marginLeft: 4,
    fontSize: 11,
    color: '#6B7280',
  },
  candidateActions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  actionButtonText: {
    marginLeft: 4,
    fontSize: 11,
    color: '#6B7280',
    fontWeight: '500',
  },
  moreButton: {
    padding: 6,
    marginLeft: 'auto',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    paddingHorizontal: 40,
  },
  fab: {
    position: 'absolute',
    bottom: 30,
    right: 30,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#EF4444',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});
